import { useState, useEffect } from 'react';
import { api } from '../../../lib/api';

interface FormSubmission {
  id: string;
  form_type: string;
  name: string;
  mobile: string;
  email: string;
  subject?: string;
  message?: string;
  unit_type?: string;
  country_code?: string;
  submitted_at: string;
  status: string;
  notes?: string;
}

const FormManager = () => {
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [selectedSubmission, setSelectedSubmission] = useState<FormSubmission | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (!isModalOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsModalOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen]);
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('new');

  useEffect(() => {
    fetchSubmissions();
  }, [filterType, filterStatus]);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const data = await api.get('/forms');
      
      let filteredData = data || [];
      
      if (filterType !== 'all') {
        filteredData = filteredData.filter((item: FormSubmission) => item.form_type === filterType);
      }

      if (filterStatus !== 'all') {
        filteredData = filteredData.filter((item: FormSubmission) => item.status === filterStatus);
      }

      setSubmissions(filteredData);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    if (submissions.length === 0) {
      alert('No data found to export.');
      return;
    }

    const headers = [
      'Date',
      'Form Type',
      'Name',
      'Phone',
      'Email',
      'Subject',
      'Message',
      'Unit of Interest',
      'Status',
      'Notes'
    ];

    const getStatusText = (status: string) => {
      switch (status) {
        case 'new': return 'New';
        case 'contacted': return 'Contacted';
        case 'completed': return 'Completed';
        case 'cancelled': return 'Cancelled';
        default: return status;
      }
    };

    const getFormTypeText = (type: string) => {
      switch (type) {
        case 'contact': return 'Contact Form';
        case 'register_interest': return 'Register Interest';
        default: return type;
      }
    };

    const escapeCSVValue = (val: string | undefined | null) => {
      if (val === undefined || val === null) return '""';
      const str = String(val).replace(/"/g, '""');
      return `"${str}"`;
    };

    const csvRows = [
      headers.join(';'),
      ...submissions.map(sub => [
        escapeCSVValue(formatDate(sub.submitted_at)),
        escapeCSVValue(getFormTypeText(sub.form_type)),
        escapeCSVValue(sub.name),
        escapeCSVValue(sub.mobile),
        escapeCSVValue(sub.email),
        escapeCSVValue(sub.subject),
        escapeCSVValue(sub.message),
        escapeCSVValue(sub.unit_type),
        escapeCSVValue(getStatusText(sub.status)),
        escapeCSVValue(sub.notes)
      ].join(';'))
    ];

    const csvContent = csvRows.join('\n');
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `form_submissions_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openSubmissionModal = (submission: FormSubmission) => {
    setSelectedSubmission(submission);
    setNotes(submission.notes || '');
    setStatus(submission.status);
    setIsModalOpen(true);
  };

  const updateSubmission = async () => {
    if (!selectedSubmission) return;

    try {
      await api.put(`/forms/${selectedSubmission.id}`, { status, notes });

      setIsModalOpen(false);
      fetchSubmissions();
    } catch (error) {
      console.error('Error updating submission:', error);
    }
  };

  const deleteSubmission = async (id: string) => {
    if (!confirm('Are you sure you want to delete this submission?')) return;

    try {
      await api.delete(`/forms/${id}`);
      fetchSubmissions();
    } catch (error) {
      console.error('Error deleting submission:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const statusColors: { [key: string]: string } = {
      new: 'bg-blue-500/20 text-blue-400 border-blue-500',
      contacted: 'bg-yellow-500/20 text-yellow-400 border-yellow-500',
      completed: 'bg-green-500/20 text-green-400 border-green-500',
      cancelled: 'bg-red-500/20 text-red-400 border-red-500'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs border ${statusColors[status] || statusColors.new}`}>
        {status === 'new' && 'New'}
        {status === 'contacted' && 'Contacted'}
        {status === 'completed' && 'Completed'}
        {status === 'cancelled' && 'Cancelled'}
      </span>
    );
  };

  const getFormTypeBadge = (type: string) => {
    const typeColors: { [key: string]: string } = {
      contact: 'bg-purple-500/20 text-purple-400 border-purple-500',
      register_interest: 'bg-indigo-500/20 text-indigo-400 border-indigo-500'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs border ${typeColors[type] || typeColors.contact}`}>
        {type === 'contact' && 'Contact Form'}
        {type === 'register_interest' && 'Register Interest'}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-light text-white">
          Form <span className="font-bold">Submissions</span>
        </h2>
        <div className="flex gap-3">
          <button
            onClick={exportToExcel}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition-colors cursor-pointer whitespace-nowrap flex items-center"
          >
            <i className="ri-file-excel-2-line mr-2"></i>
            Excel Export
          </button>
          <button
            onClick={fetchSubmissions}
            className="px-4 py-2 bg-[#C5A46D] text-black rounded hover:bg-[#B8956A] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line mr-2"></i>
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-400 mb-2">Form Type</label>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white focus:border-[#C5A46D] outline-none cursor-pointer"
          >
            <option value="all">All</option>
            <option value="contact">Contact Form</option>
            <option value="register_interest">Register Interest</option>
          </select>
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-2">Status</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white focus:border-[#C5A46D] outline-none cursor-pointer"
          >
            <option value="all">All</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Submissions Table */}
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400">
            <i className="ri-loader-4-line text-3xl animate-spin"></i>
            <p className="mt-2">Loading...</p>
          </div>
        ) : submissions.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <i className="ri-inbox-line text-5xl mb-4"></i>
            <p>No submissions found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Form Type
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {submissions.map((submission) => (
                  <tr key={submission.id} className="hover:bg-gray-700/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {formatDate(submission.submitted_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getFormTypeBadge(submission.form_type)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                      {submission.name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-300">
                      <div>{submission.mobile}</div>
                      <div className="text-xs text-gray-400">{submission.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(submission.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => openSubmissionModal(submission)}
                        className="text-[#C5A46D] hover:text-white mr-4 cursor-pointer"
                        title="View"
                      >
                        <i className="ri-eye-line text-lg"></i>
                      </button>
                      <button
                        onClick={() => deleteSubmission(submission.id)}
                        className="text-red-400 hover:text-red-300 cursor-pointer"
                        title="Delete"
                      >
                        <i className="ri-delete-bin-line text-lg"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Submission Detail Modal */}
      {isModalOpen && selectedSubmission && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className="bg-gray-900 border border-gray-700 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-6 border-b border-gray-700">
              <h3 className="text-xl font-light text-white">
                Submission <span className="font-bold">Details</span>
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Form Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Form Type</label>
                  {getFormTypeBadge(selectedSubmission.form_type)}
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Submitted Date</label>
                  <p className="text-white">{formatDate(selectedSubmission.submitted_at)}</p>
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Name</label>
                  <p className="text-white">{selectedSubmission.name}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Phone</label>
                    <p className="text-white">{selectedSubmission.mobile}</p>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Email</label>
                    <p className="text-white">{selectedSubmission.email}</p>
                  </div>
                </div>
              </div>

              {/* Form Specific Fields */}
              {selectedSubmission.subject && (
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Subject</label>
                  <p className="text-white">{selectedSubmission.subject}</p>
                </div>
              )}

              {selectedSubmission.message && (
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Message</label>
                  <p className="text-white bg-gray-800 p-4 rounded">{selectedSubmission.message}</p>
                </div>
              )}

              {selectedSubmission.unit_type && (
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Unit of Interest</label>
                  <p className="text-white">{selectedSubmission.unit_type}</p>
                </div>
              )}

              {/* Status Update */}
              <div>
                <label className="block text-xs text-gray-400 mb-2">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white focus:border-[#C5A46D] outline-none cursor-pointer"
                >
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs text-gray-400 mb-2">Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white focus:border-[#C5A46D] outline-none resize-none"
                  placeholder="Enter internal notes here..."
                />
              </div>

              {/* Actions */}
              <div className="flex gap-4">
                <button
                  onClick={updateSubmission}
                  className="flex-1 bg-[#C5A46D] text-black py-3 rounded hover:bg-[#B8956A] transition-colors cursor-pointer whitespace-nowrap font-medium"
                >
                  Save
                </button>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 bg-gray-700 text-white py-3 rounded hover:bg-gray-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormManager;
